import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Calculator,
  CheckCircle2,
  Clock,
  FileText,
  MapPinned,
  Search,
  ShieldCheck,
  Sparkles,
  Star,
  X,
} from 'lucide-react';
import TopStatsNav from './TopStatsNav';
import MapIrMap from './MapIrMap';
import { ROUTES } from '../lib/routes';

const nfFa = new Intl.NumberFormat('fa-IR');

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function toNumberOrZero(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function pillBase(cls) {
  return `inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-[11px] font-extrabold border ${cls}`;
}

function ctaPrimary(cls) {
  return `h-12 px-5 rounded-2xl ${cls} text-white font-bold text-sm transition inline-flex items-center justify-center gap-2 shadow-lg`;
}

function ctaSecondary() {
  return 'h-12 px-5 rounded-2xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 font-bold text-sm transition inline-flex items-center justify-center gap-2';
}

function estimateForService({ baseDays, baseCost }, { weightKg, volumeM3, isExpress, needsInsurance, isSpecialHandling }) {
  const w = clamp(toNumberOrZero(weightKg), 0, 250000);
  const v = clamp(toNumberOrZero(volumeM3), 0, 500);

  const volumeFactor = 1 + v / 60;
  const weightFactor = 1 + w / 25000;
  const specialFactor = isSpecialHandling ? 1.15 : 1;
  const insuranceFactor = needsInsurance ? 1.06 : 1;
  const expressFactor = isExpress ? 0.78 : 1;

  const days = clamp(Math.round(baseDays * expressFactor + (w > 0 ? 1 : 0)), 1, 30);
  const costIndex = Math.round(baseCost * volumeFactor * weightFactor * specialFactor * insuranceFactor);

  return {
    days,
    costIndex,
    breakdown: {
      volumeFactor,
      weightFactor,
      specialFactor,
      insuranceFactor,
      expressFactor,
    },
  };
}

export default function DirectServiceTemplate({
  meta,
  accent,
  formDefaults,
  packages,
  capabilities,
  documents,
  processSteps,
  providers,
  hubs,
  faqs,
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [providerQuery, setProviderQuery] = useState('');
  const [selectedPackageKey, setSelectedPackageKey] = useState(packages?.[0]?.key || 'standard');
  const [modalOpen, setModalOpen] = useState(false);

  const [form, setForm] = useState(() => ({
    origin: formDefaults?.origin || '',
    destination: formDefaults?.destination || '',
    cargo: formDefaults?.cargo || '',
    incoterm: formDefaults?.incoterm || 'CPT',
    weightKg: formDefaults?.weightKg ?? 1000,
    volumeM3: formDefaults?.volumeM3 ?? 4,
    isExpress: !!formDefaults?.isExpress,
    needsInsurance: !!formDefaults?.needsInsurance,
    isSpecialHandling: !!formDefaults?.isSpecialHandling,
  }));

  useEffect(() => {
    // Leaflet icons not needed; kept as a placeholder for potential future Leaflet usage.
  }, []);

  const selectedPackage = useMemo(
    () => packages.find((p) => p.key === selectedPackageKey) || packages[0],
    [packages, selectedPackageKey]
  );

  const effectiveBase = useMemo(() => {
    const p = selectedPackage || { baseDays: meta.baseDays, baseCost: meta.baseCost };
    return {
      baseDays: p.baseDays ?? meta.baseDays,
      baseCost: p.baseCost ?? meta.baseCost,
    };
  }, [selectedPackage, meta.baseDays, meta.baseCost]);

  const est = useMemo(
    () =>
      estimateForService(
        { baseDays: effectiveBase.baseDays, baseCost: effectiveBase.baseCost },
        {
          weightKg: form.weightKg,
          volumeM3: form.volumeM3,
          isExpress: form.isExpress,
          needsInsurance: form.needsInsurance,
          isSpecialHandling: form.isSpecialHandling,
        }
      ),
    [effectiveBase.baseDays, effectiveBase.baseCost, form]
  );

  const filteredProviders = useMemo(() => {
    const q = String(providerQuery || '').trim();
    if (!q) return providers;
    return providers.filter((p) => (p.name + p.badges.join(' ') + p.note).includes(q));
  }, [providers, providerQuery]);

  const filteredDocs = useMemo(() => {
    const q = String(query || '').trim();
    if (!q) return documents;
    return documents.filter((d) => (d.title + d.desc + d.tags.join(' ')).includes(q));
  }, [documents, query]);

  const mapPath = useMemo(() => {
    const pts = (hubs || []).slice(0, 4).map((h) => [h.lat, h.lng]);
    return pts.length >= 2 ? pts : [[32.0, 53.7], [35.7, 51.4]];
  }, [hubs]);

  const mapCenter = mapPath[Math.floor(mapPath.length / 2)] || [32.0, 53.7];

  return (
    <div className="min-h-screen bg-[#f8fafc] font-[IRANSansWeb,Tahoma] p-6 md:p-10" dir="rtl">
      <div className="max-w-6xl mx-auto space-y-8">
        <header className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-8 md:p-10 overflow-hidden relative">
          <div className={`absolute inset-0 opacity-40 ${accent.heroBg}`} />
          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div className="text-right">
              <div className={pillBase(accent.pill)}>
                <meta.Icon size={16} />
                <span>{meta.badge}</span>
              </div>

              <h1 className="mt-6 text-3xl md:text-4xl font-black text-gray-900 leading-tight">{meta.title}</h1>
              <p className="mt-4 text-sm text-gray-500 leading-8">{meta.subtitle}</p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <button type="button" onClick={() => navigate('/dashboard')} className={ctaSecondary()}>
                  <ArrowRight size={18} />
                  بازگشت به داشبورد
                </button>
                <button type="button" onClick={() => navigate(ROUTES.systems.tracking)} className={ctaPrimary(accent.cta)}>
                  رفتن به رهگیری چندوجهی
                  <ArrowLeft size={18} />
                </button>
              </div>
            </div>

            <div className="bg-[#fbfdff] border border-gray-100 rounded-[2.5rem] p-6 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div className="text-right">
                  <div className="text-[11px] text-gray-400 font-bold">پیشنهاد سریع</div>
                  <div className="mt-2 text-lg font-black text-gray-800">{meta.quickTitle}</div>
                  <div className="mt-1 text-[11px] text-gray-500 leading-6">{meta.quickDesc}</div>
                </div>
                <div className={`w-12 h-12 rounded-2xl border flex items-center justify-center ${accent.kpiBox}`}>
                  <Sparkles size={22} />
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3">
                <MiniKpi label="SLA پیشنهادی" value={meta.sla} icon={<ShieldCheck size={16} />} accent={accent} />
                <MiniKpi label="زمان تقریبی" value={`${nfFa.format(est.days)} روز`} icon={<Clock size={16} />} accent={accent} />
                <MiniKpi label="شاخص هزینه" value={nfFa.format(est.costIndex)} icon={<Calculator size={16} />} accent={accent} />
                <MiniKpi label="بسته منتخب" value={selectedPackage?.title || '—'} icon={<BadgeCheck size={16} />} accent={accent} />
              </div>
            </div>
          </div>
        </header>

        <TopStatsNav
          items={[
            {
              label: 'مجوزها / مدل‌های مشارکت',
              value: 3,
              unit: 'مدل رایج',
              route: ROUTES.systems.permitsPartnership,
              accent: 'amber',
              sparkValues: [2, 3, 3, 4, 5, 6],
            },
            {
              label: 'رهگیری چندوجهی',
              value: 3,
              unit: 'پرونده نمونه',
              route: ROUTES.systems.tracking,
              accent: 'blue',
              sparkValues: [3, 4, 6, 6, 7, 9],
            },
          ]}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <section className="lg:col-span-2 bg-white rounded-[2.5rem] border border-gray-100 shadow-sm overflow-hidden">
            <div className="p-6 md:p-8 border-b border-gray-100">
              <div className="flex items-start justify-between gap-6">
                <div className="text-right">
                  <h2 className="text-lg font-black text-gray-800">ثبت سفارش (نمونه)</h2>
                  <p className="text-[11px] text-gray-400 mt-2 leading-6">
                    فرم نمونه برای ساخت RFQ/سفارش. در نسخه واقعی به API متصل می‌شود و برآوردها براساس تعرفه و ظرفیت به‌روز می‌شوند.
                  </p>
                </div>
                <div className={`w-12 h-12 rounded-2xl border flex items-center justify-center ${accent.kpiBox}`}>
                  <MapPinned size={22} />
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField
                  label="مبدا"
                  value={form.origin}
                  placeholder={meta.placeholders.origin}
                  onChange={(v) => setForm((s) => ({ ...s, origin: v }))}
                />
                <TextField
                  label="مقصد"
                  value={form.destination}
                  placeholder={meta.placeholders.destination}
                  onChange={(v) => setForm((s) => ({ ...s, destination: v }))}
                />
                <TextField
                  label="نوع کالا"
                  value={form.cargo}
                  placeholder={meta.placeholders.cargo}
                  onChange={(v) => setForm((s) => ({ ...s, cargo: v }))}
                />
                <SelectField
                  label="اینکوترمز"
                  value={form.incoterm}
                  options={['EXW', 'FCA', 'FOB', 'CPT', 'CIF', 'DAP', 'DDP']}
                  onChange={(v) => setForm((s) => ({ ...s, incoterm: v }))}
                />
                <NumberField
                  label="وزن (کیلوگرم)"
                  value={form.weightKg}
                  onChange={(v) => setForm((s) => ({ ...s, weightKg: v }))}
                />
                <NumberField
                  label="حجم (مترمکعب)"
                  value={form.volumeM3}
                  onChange={(v) => setForm((s) => ({ ...s, volumeM3: v }))}
                />
              </div>

              <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
                <Toggle
                  checked={form.isExpress}
                  onChange={(v) => setForm((s) => ({ ...s, isExpress: v }))}
                  title="اکسپرس / اولویت"
                  desc="برای زمان کمتر (در صورت ظرفیت)."
                  accent={accent}
                />
                <Toggle
                  checked={form.needsInsurance}
                  onChange={(v) => setForm((s) => ({ ...s, needsInsurance: v }))}
                  title="بیمه محموله"
                  desc="پیشنهادی برای کاهش ریسک."
                  accent={accent}
                />
                <Toggle
                  checked={form.isSpecialHandling}
                  onChange={(v) => setForm((s) => ({ ...s, isSpecialHandling: v }))}
                  title="شرایط ویژه"
                  desc="DG/دما/ابعاد خاص و..."
                  accent={accent}
                />
              </div>

              <div className="mt-6 rounded-2xl border border-gray-100 bg-[#fbfdff] p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="text-right">
                    <div className="text-sm font-black text-gray-800">برآورد سریع</div>
                    <div className="text-[11px] text-gray-500 mt-2 leading-7">
                      این برآورد «شاخصی» است و صرفاً برای نمایش UX طراحی شده است.
                    </div>
                  </div>
                  <div className={`w-10 h-10 rounded-2xl border flex items-center justify-center ${accent.kpiBox}`}>
                    <Calculator size={18} />
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <KpiRow label="زمان تقریبی" value={`${nfFa.format(est.days)} روز`} />
                  <KpiRow label="شاخص هزینه" value={nfFa.format(est.costIndex)} />
                  <KpiRow label="ریسک/کنترل" value={form.isSpecialHandling ? 'نیازمند کنترل' : 'استاندارد'} />
                </div>

                <div className="mt-4 flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    onClick={() => setModalOpen(true)}
                    className={`h-12 px-6 rounded-2xl ${accent.orderCta} font-black transition inline-flex items-center justify-center gap-2`}
                  >
                    ایجاد درخواست قیمت (RFQ)
                    <ArrowLeft size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(ROUTES.systems.tracking)}
                    className={ctaSecondary()}
                  >
                    مشاهده رهگیری
                    <ArrowLeft size={18} />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6 md:p-8">
              <div className="flex items-start justify-between gap-6">
                <div className="text-right">
                  <h3 className="text-lg font-black text-gray-800">نقشه هاب‌ها و شبکه (نمونه)</h3>
                  <p className="text-[11px] text-gray-400 mt-2 leading-6">
                    نمایش نمونه‌ای از گره‌های اصلی شبکه و یک مسیر پیشنهادی. در نسخه واقعی می‌تواند پوشش‌دهی، ظرفیت و وضعیت را نشان دهد.
                  </p>
                </div>
                <div className={`w-12 h-12 rounded-2xl border flex items-center justify-center ${accent.kpiBox}`}>
                  <MapPinned size={22} />
                </div>
              </div>

              <div className="mt-6 h-[340px] rounded-2xl overflow-hidden border border-gray-100">
                <MapIrMap
                  className="h-full w-full"
                  centerLatLng={mapCenter}
                  zoom={4}
                  lineLatLngs={mapPath}
                  lineColor={accent.polyline}
                  lineWidth={4}
                  markers={(hubs || []).map((h) => ({
                    id: h.name,
                    lat: h.lat,
                    lng: h.lng,
                    title: h.name,
                    subtitle: h.note,
                  }))}
                />
              </div>
            </div>
          </section>

          <aside className="space-y-6">
            <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-6 md:p-8">
              <div className="flex items-start justify-between gap-6">
                <div className="text-right">
                  <h3 className="text-lg font-black text-gray-800">بسته‌های خدمت</h3>
                  <p className="text-[11px] text-gray-400 mt-2 leading-6">انتخاب بسته برای سطح SLA و خدمات اضافه.</p>
                </div>
                <div className={`w-12 h-12 rounded-2xl border flex items-center justify-center ${accent.kpiBox}`}>
                  <Star size={22} />
                </div>
              </div>

              <div className="mt-6 space-y-3">
                {packages.map((p) => {
                  const active = p.key === selectedPackageKey;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => setSelectedPackageKey(p.key)}
                      className={`w-full text-right p-4 rounded-2xl border transition ${
                        active ? accent.packageActive : 'border-gray-100 bg-[#fbfdff] hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-[12px] font-black text-gray-800">{p.title}</div>
                          <div className="text-[11px] text-gray-500 mt-1 leading-6">{p.desc}</div>
                        </div>
                        <span className={`text-[10px] font-extrabold px-3 py-1.5 rounded-full border ${p.badgeCls}`}>
                          {p.badge}
                        </span>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-2 text-[10px] text-gray-500 font-bold">
                        <div className="rounded-xl bg-white border border-gray-100 px-3 py-2 flex items-center justify-between gap-3">
                          <span>زمان پایه</span>
                          <span className="text-gray-800 font-extrabold">{nfFa.format(p.baseDays)} روز</span>
                        </div>
                        <div className="rounded-xl bg-white border border-gray-100 px-3 py-2 flex items-center justify-between gap-3">
                          <span>شاخص هزینه</span>
                          <span className="text-gray-800 font-extrabold">{nfFa.format(p.baseCost)}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-6 md:p-8">
              <div className="flex items-start justify-between gap-6">
                <div className="text-right">
                  <h3 className="text-lg font-black text-gray-800">قابلیت‌ها</h3>
                  <p className="text-[11px] text-gray-400 mt-2 leading-6">ویژگی‌های کلیدی قابل ارائه در این مد حمل.</p>
                </div>
                <div className={`w-12 h-12 rounded-2xl border flex items-center justify-center ${accent.kpiBox}`}>
                  <ShieldCheck size={22} />
                </div>
              </div>

              <div className="mt-6 space-y-3">
                {(capabilities || []).map((c) => (
                  <div key={c.title} className="rounded-2xl border border-gray-100 bg-[#fbfdff] p-4">
                    <div className="text-[12px] font-extrabold text-gray-800">{c.title}</div>
                    <div className="text-[11px] text-gray-500 mt-2 leading-7">{c.desc}</div>
                    {!!c.items?.length && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {c.items.map((x) => (
                          <span
                            key={x}
                            className="text-[10px] font-extrabold bg-white border border-gray-100 text-gray-600 px-3 py-1.5 rounded-full"
                          >
                            {x}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <section className="lg:col-span-2 bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-6 md:p-8">
            <div className="flex items-start justify-between gap-6">
              <div className="text-right">
                <h3 className="text-lg font-black text-gray-800">فرآیند اجرای خدمت</h3>
                <p className="text-[11px] text-gray-400 mt-2 leading-6">
                  مراحل عملیاتی نمونه از ثبت RFQ تا تحویل/تسویه (قابل سفارشی‌سازی براساس SLA).
                </p>
              </div>
              <div className={`w-12 h-12 rounded-2xl border flex items-center justify-center ${accent.kpiBox}`}>
                <CheckCircle2 size={22} />
              </div>
            </div>

            <div className="mt-6 space-y-3">
              {(processSteps || []).map((s, idx) => (
                <div key={s.title} className="flex items-start gap-3 rounded-2xl border border-gray-100 bg-[#fbfdff] p-4">
                  <div className={`w-9 h-9 rounded-2xl border flex items-center justify-center font-black text-xs flex-shrink-0 ${accent.stepBox}`}>
                    {(idx + 1).toLocaleString('fa-IR')}
                  </div>
                  <div className="min-w-0">
                    <div className="text-[12px] font-extrabold text-gray-800">{s.title}</div>
                    <div className="text-[11px] text-gray-500 mt-1 leading-7">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <aside className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-6 md:p-8">
            <div className="flex items-start justify-between gap-6">
              <div className="text-right">
                <h3 className="text-lg font-black text-gray-800">مدارک و چک‌لیست</h3>
                <p className="text-[11px] text-gray-400 mt-2 leading-6">برای کاهش برگشت پرونده، موارد را آماده کنید.</p>
              </div>
              <div className={`w-12 h-12 rounded-2xl border flex items-center justify-center ${accent.kpiBox}`}>
                <FileText size={22} />
              </div>
            </div>

            <div className="mt-6 relative">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="جستجو در مدارک..."
                className={`w-full h-12 pr-12 pl-4 rounded-2xl border border-gray-200 bg-white focus:outline-none ${accent.inputFocus} text-sm`}
              />
              <Search className="absolute right-4 top-3 text-gray-400" size={20} />
            </div>

            <div className="mt-5 space-y-3">
              {filteredDocs.map((d) => (
                <div key={d.title} className="rounded-2xl border border-gray-100 bg-[#fbfdff] p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="text-right min-w-0">
                      <div className="text-[12px] font-extrabold text-gray-800">{d.title}</div>
                      <div className="text-[11px] text-gray-500 mt-1 leading-7">{d.desc}</div>
                    </div>
                    <span className={`text-[10px] font-extrabold px-3 py-1.5 rounded-full border ${d.stateCls}`}>
                      {d.state}
                    </span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(d.tags || []).map((t) => (
                      <span
                        key={`${d.title}-${t}`}
                        className="text-[10px] font-extrabold bg-white border border-gray-100 text-gray-600 px-3 py-1.5 rounded-full"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </aside>
        </div>

        <section className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-6 md:p-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="text-right">
              <h3 className="text-lg font-black text-gray-800">اپراتورها / سرویس‌دهندگان (نمونه)</h3>
              <p className="text-[11px] text-gray-400 mt-2 leading-6">
                لیست نمونه برای نمایش UI بازارچه. در نسخه واقعی از دایرکتوری شرکت‌ها و ظرفیت لحظه‌ای تغذیه می‌شود.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate(ROUTES.systems.permitsPartnership)}
              className={`h-12 px-6 rounded-2xl ${accent.orderCta} font-black transition inline-flex items-center gap-2`}
            >
              مشاهده مجوز/مشارکت
              <ArrowLeft size={18} />
            </button>
          </div>

          <div className="mt-6 relative">
            <input
              value={providerQuery}
              onChange={(e) => setProviderQuery(e.target.value)}
              placeholder="جستجو: نام اپراتور، برچسب‌ها..."
              className={`w-full h-12 pr-12 pl-4 rounded-2xl border border-gray-200 bg-white focus:outline-none ${accent.inputFocus} text-sm`}
            />
            <Search className="absolute right-4 top-3 text-gray-400" size={20} />
          </div>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredProviders.map((p) => (
              <div key={p.name} className="rounded-2xl border border-gray-100 bg-[#fbfdff] p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="text-right min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-black text-gray-800 truncate">{p.name}</div>
                      <span className={`text-[10px] font-extrabold px-3 py-1.5 rounded-full border ${p.scoreCls}`}>
                        امتیاز {nfFa.format(p.score)}
                      </span>
                    </div>
                    <div className="text-[11px] text-gray-500 mt-2 leading-7">{p.note}</div>
                  </div>
                  <div className={`w-11 h-11 rounded-2xl border flex items-center justify-center ${accent.kpiBox} flex-shrink-0`}>
                    <p.Icon size={20} />
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {(p.badges || []).map((b) => (
                    <span
                      key={`${p.name}-${b}`}
                      className="text-[10px] font-extrabold bg-white border border-gray-100 text-gray-600 px-3 py-1.5 rounded-full"
                    >
                      {b}
                    </span>
                  ))}
                </div>

                <div className="mt-4 grid grid-cols-3 gap-2 text-[10px] text-gray-500 font-bold">
                  <SmallStat label="زمان پاسخ" value={p.response} />
                  <SmallStat label="پوشش" value={p.coverage} />
                  <SmallStat label="ظرفیت" value={p.capacity} />
                </div>

                <div className="mt-4 flex gap-3">
                  <button type="button" onClick={() => setModalOpen(true)} className={`h-11 px-4 rounded-2xl ${accent.orderCta} font-black transition flex-1`}>
                    درخواست قیمت
                  </button>
                  <button type="button" onClick={() => navigate(ROUTES.systems.tracking)} className={`h-11 px-4 rounded-2xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 font-bold text-sm transition`}>
                    رهگیری
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-6 md:p-8">
          <div className="flex items-start justify-between gap-6">
            <div className="text-right">
              <h3 className="text-lg font-black text-gray-800">پرسش‌های پرتکرار</h3>
              <p className="text-[11px] text-gray-400 mt-2 leading-6">پاسخ‌های کوتاه برای شروع سریع‌تر.</p>
            </div>
            <div className={`w-12 h-12 rounded-2xl border flex items-center justify-center ${accent.kpiBox}`}>
              <BadgeCheck size={22} />
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {(faqs || []).map((f) => (
              <div key={f.q} className="rounded-2xl border border-gray-100 bg-[#fbfdff] p-5">
                <div className="text-sm font-extrabold text-gray-800">{f.q}</div>
                <div className="text-[11px] text-gray-500 mt-2 leading-7">{f.a}</div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 z-0 bg-slate-900/50"
            onClick={() => setModalOpen(false)}
            aria-label="بستن"
          />
          <div className="relative z-10 w-full max-w-2xl bg-white rounded-[2.5rem] border border-gray-100 shadow-2xl overflow-hidden">
            <div className={`p-6 md:p-8 text-white ${accent.modalHeader}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="text-right">
                  <div className="text-[11px] font-extrabold text-white/80">RFQ (نمونه)</div>
                  <div className="mt-2 text-xl font-black">{meta.title} — درخواست قیمت</div>
                  <div className="mt-2 text-[11px] text-white/80 leading-6">
                    این پنجره صرفاً برای نمایش تجربه کاربری است و به API متصل نیست.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="w-10 h-10 rounded-2xl bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center"
                  aria-label="بستن"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="p-6 md:p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <SummaryRow label="مبدا" value={form.origin || '—'} />
                <SummaryRow label="مقصد" value={form.destination || '—'} />
                <SummaryRow label="نوع کالا" value={form.cargo || '—'} />
                <SummaryRow label="اینکوترمز" value={form.incoterm || '—'} />
                <SummaryRow label="وزن" value={`${nfFa.format(toNumberOrZero(form.weightKg))} کیلوگرم`} />
                <SummaryRow label="حجم" value={`${nfFa.format(toNumberOrZero(form.volumeM3))} مترمکعب`} />
                <SummaryRow label="بسته" value={selectedPackage?.title || '—'} />
                <SummaryRow label="زمان/هزینه" value={`${nfFa.format(est.days)} روز | شاخص ${nfFa.format(est.costIndex)}`} />
              </div>

              <div className="mt-6 rounded-2xl border border-gray-100 bg-[#fbfdff] p-5">
                <div className="text-sm font-black text-gray-800">خدمات انتخاب‌شده</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Chip active={form.isExpress} label="اکسپرس" />
                  <Chip active={form.needsInsurance} label="بیمه" />
                  <Chip active={form.isSpecialHandling} label="شرایط ویژه" />
                </div>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setModalOpen(false);
                    navigate(ROUTES.systems.tracking);
                  }}
                  className={`h-12 px-6 rounded-2xl ${accent.orderCta} font-black transition inline-flex items-center justify-center gap-2`}
                >
                  رفتن به رهگیری
                  <ArrowLeft size={18} />
                </button>
                <button type="button" onClick={() => setModalOpen(false)} className={ctaSecondary()}>
                  بستن
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MiniKpi({ label, value, icon, accent }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 flex items-start justify-between gap-4">
      <div className="text-right min-w-0">
        <div className="text-[10px] text-gray-400 font-bold">{label}</div>
        <div className="text-[12px] font-extrabold text-gray-800 mt-1 leading-6">{value}</div>
      </div>
      <div className={`w-10 h-10 rounded-2xl border flex items-center justify-center flex-shrink-0 ${accent.kpiBox}`}>
        {icon}
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, placeholder }) {
  return (
    <div className="space-y-2">
      <div className="text-[11px] text-gray-400 font-bold">{label}</div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-12 px-4 rounded-2xl border border-gray-200 bg-white focus:outline-none focus:border-gray-400 text-sm"
      />
    </div>
  );
}

function NumberField({ label, value, onChange }) {
  return (
    <div className="space-y-2">
      <div className="text-[11px] text-gray-400 font-bold">{label}</div>
      <input
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-12 px-4 rounded-2xl border border-gray-200 bg-white focus:outline-none focus:border-gray-400 text-sm"
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div className="space-y-2">
      <div className="text-[11px] text-gray-400 font-bold">{label}</div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-12 px-4 rounded-2xl border border-gray-200 bg-white focus:outline-none focus:border-gray-400 text-sm"
      >
        {(options || []).map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

function Toggle({ checked, onChange, title, desc, accent }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`text-right p-4 rounded-2xl border transition ${checked ? accent.toggleOn : 'border-gray-100 bg-[#fbfdff] hover:shadow-sm'}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[12px] font-black text-gray-800">{title}</div>
          <div className="text-[11px] text-gray-500 mt-1 leading-6">{desc}</div>
        </div>
        <div
          className={`w-12 h-7 rounded-full border flex items-center transition px-1 ${
            checked ? accent.switchTrack : 'bg-white border-gray-200'
          }`}
        >
          <div className={`w-5 h-5 rounded-full transition ${checked ? accent.switchKnobOn : 'bg-gray-200 translate-x-0'}`} style={{ transform: checked ? 'translateX(-18px)' : 'translateX(0px)' }} />
        </div>
      </div>
    </button>
  );
}

function KpiRow({ label, value }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 flex items-center justify-between gap-4">
      <div className="text-[11px] text-gray-400 font-bold">{label}</div>
      <div className="text-[12px] font-extrabold text-gray-800">{value}</div>
    </div>
  );
}

function SmallStat({ label, value }) {
  return (
    <div className="rounded-xl bg-white border border-gray-100 px-3 py-2">
      <div className="text-[10px] text-gray-400 font-bold">{label}</div>
      <div className="text-[11px] text-gray-800 font-extrabold mt-1">{value}</div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 flex items-center justify-between gap-3">
      <div className="text-[11px] text-gray-400 font-bold">{label}</div>
      <div className="text-[12px] font-extrabold text-gray-800">{value}</div>
    </div>
  );
}

function Chip({ active, label }) {
  return (
    <span
      className={`text-[10px] font-extrabold px-3 py-1.5 rounded-full border ${
        active ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-white text-gray-400 border-gray-100'
      }`}
    >
      {label}
    </span>
  );
}
